export const TRANSPORTATION_PRESET_TYPE = 'transportationPreset';
/** Child questions that may be attached from multiple parent options (builder + backend routes). */
export const TRANSPORT_MULTI_PARENT_TARGET_METAS = new Set([
    'preset.transport.pickupLocationTerminal',
    'preset.transport.accommodationRequired',
    'preset.transport.bookingRequired'
]);
export const createTransportationPresetFields = ({ startingFieldOrder, pickupLocations = [], t }) => {
    const formatPickupTimeLabel = (timeOfPickup) => {
        if (!timeOfPickup)
            return '';
        const value = String(timeOfPickup).trim();
        const match = /T(\d{2}):(\d{2})/.exec(value) ?? /^(\d{1,2}):(\d{2})/.exec(value);
        if (!match)
            return '';
        return `${match[1].padStart(2, '0')}:${match[2]}`;
    };
    const pickupLocationOptions = pickupLocations.map((location, idx) => ({
        id: String(location.id || crypto.randomUUID()),
        label: (() => {
            const name = String(location.name || `Pickup ${idx + 1}`);
            const formattedTime = formatPickupTimeLabel(location.timeOfPickup);
            return formattedTime ? `${name} (${formattedTime})` : name;
        })(),
        value: idx + 1,
        isFromBackend: true
    }));
    const arrivalQuestionId = crypto.randomUUID();
    const pickupLocationId = crypto.randomUUID();
    const accommodationRequiredId = crypto.randomUUID();
    const bookingRequiredId = crypto.randomUUID();
    const accommodationWhereId = crypto.randomUUID();
    const mobilityRequiredId = crypto.randomUUID();
    const mobilityDetailsId = crypto.randomUUID();
    const busOptionId = crypto.randomUUID();
    const trainOptionId = crypto.randomUUID();
    const commercialFlightOptionId = crypto.randomUUID();
    const yesAccommodationOptionId = crypto.randomUUID();
    const noAccommodationOptionId = crypto.randomUUID();
    const yesMobilityOptionId = crypto.randomUUID();
    const noMobilityOptionId = crypto.randomUUID();
    const yesBookingOptionId = crypto.randomUUID();
    const noBookingOptionId = crypto.randomUUID();
    return [
        {
            id: arrivalQuestionId,
            fieldParent: 'Choice (Radio)',
            fieldType: 'radio',
            label: t('events.transportPreset.selectArrivalTransport'),
            descriptionForToolTip: '',
            required: true,
            fieldOrder: startingFieldOrder,
            fieldAdditionalNote: 'preset.transport.arrivalTransport',
            isIdentity: true,
            isFromBackend: false,
            isBranchingEnabled: true,
            options: [
                {
                    id: busOptionId,
                    label: t('events.transportPreset.arrivalBy.bus'),
                    value: 1,
                    isFromBackend: false,
                    optionChainingShowField: [pickupLocationId]
                },
                {
                    id: trainOptionId,
                    label: t('events.transportPreset.arrivalBy.train'),
                    value: 2,
                    isFromBackend: false,
                    optionChainingShowField: []
                },
                {
                    id: commercialFlightOptionId,
                    label: t('events.transportPreset.arrivalBy.commercialFlight'),
                    value: 3,
                    isFromBackend: false,
                    optionChainingShowField: []
                }
            ]
        },
        {
            id: pickupLocationId,
            fieldParent: 'Pickup Location Dropdown',
            fieldType: 'pickupLocation',
            label: t('events.transportPreset.pickupLocationTerminal'),
            descriptionForToolTip: '',
            required: true,
            fieldOrder: startingFieldOrder + 1,
            fieldAdditionalNote: 'preset.transport.pickupLocationTerminal',
            isIdentity: true,
            isFromBackend: false,
            isBranchingEnabled: true,
            parentFieldId: arrivalQuestionId,
            parentFieldLabel: t('events.transportPreset.selectArrivalTransport'),
            parentOptionId: null,
            parentOptionIds: [],
            parentOptionLabel: t('events.transportPreset.arrivalBy.bus'),
            parentOptionValue: 1,
            options: pickupLocationOptions.map((opt) => ({
                ...opt,
                optionChainingShowField: pickupLocations.find((l) => String(l.id || '') === String(opt.id))?.isAccommodationAvailable === true
                    ? [accommodationRequiredId]
                    : []
            }))
        },
        {
            id: accommodationRequiredId,
            fieldParent: 'Choice (Radio)',
            fieldType: 'radio',
            label: t('events.transportPreset.accommodationRequired'),
            descriptionForToolTip: '',
            required: true,
            fieldOrder: startingFieldOrder + 2,
            fieldAdditionalNote: 'preset.transport.accommodationRequired',
            isIdentity: true,
            isFromBackend: false,
            isBranchingEnabled: true,
            options: [
                {
                    id: yesAccommodationOptionId,
                    label: t('events.toggleOptions.yes'),
                    value: 1,
                    isFromBackend: false,
                    optionChainingShowField: []
                },
                {
                    id: noAccommodationOptionId,
                    label: t('events.toggleOptions.no'),
                    value: 0,
                    isFromBackend: false,
                    optionChainingShowField: [bookingRequiredId]
                }
            ]
        },
        {
            id: bookingRequiredId,
            fieldParent: 'Choice (Radio)',
            fieldType: 'radio',
            label: t('events.transportPreset.bookingRequired'),
            descriptionForToolTip: '',
            required: true,
            fieldOrder: startingFieldOrder + 3,
            fieldAdditionalNote: 'preset.transport.bookingRequired',
            isIdentity: true,
            isFromBackend: false,
            parentFieldId: accommodationRequiredId,
            parentFieldLabel: t('events.transportPreset.accommodationRequired'),
            parentOptionId: null,
            parentOptionLabel: t('events.toggleOptions.no'),
            parentOptionValue: 0,
            isBranchingEnabled: true,
            options: [
                {
                    id: yesBookingOptionId,
                    label: t('events.toggleOptions.yes'),
                    value: 1,
                    isFromBackend: false,
                    optionChainingShowField: [accommodationWhereId]
                },
                {
                    id: noBookingOptionId,
                    label: t('events.toggleOptions.no'),
                    value: 0,
                    isFromBackend: false,
                    optionChainingShowField: []
                }
            ]
        },
        {
            id: accommodationWhereId,
            fieldParent: 'Single Line Input',
            fieldChild: 'Text',
            fieldType: 'text',
            label: t('events.transportPreset.accommodationWhere'),
            descriptionForToolTip: '',
            required: true,
            fieldOrder: startingFieldOrder + 4,
            fieldAdditionalNote: 'preset.transport.accommodationWhere',
            isIdentity: true,
            isFromBackend: false,
            parentFieldId: bookingRequiredId,
            parentFieldLabel: t('events.transportPreset.bookingRequired'),
            parentOptionId: null,
            parentOptionLabel: t('events.toggleOptions.yes'),
            parentOptionValue: 1
        },
        {
            id: mobilityRequiredId,
            fieldParent: 'Choice (Radio)',
            fieldType: 'radio',
            label: t('events.transportPreset.mobilityRequired'),
            descriptionForToolTip: '',
            required: true,
            fieldOrder: startingFieldOrder + 5,
            fieldAdditionalNote: 'preset.transport.mobilityRequired',
            isIdentity: true,
            isFromBackend: false,
            isBranchingEnabled: true,
            options: [
                {
                    id: yesMobilityOptionId,
                    label: t('events.toggleOptions.yes'),
                    value: 1,
                    isFromBackend: false,
                    optionChainingShowField: [mobilityDetailsId]
                },
                {
                    id: noMobilityOptionId,
                    label: t('events.toggleOptions.no'),
                    value: 0,
                    isFromBackend: false,
                    optionChainingShowField: []
                }
            ]
        },
        {
            id: mobilityDetailsId,
            fieldParent: 'Single Line Input',
            fieldChild: 'Text',
            fieldType: 'text',
            label: t('events.transportPreset.mobilityDetails'),
            descriptionForToolTip: '',
            required: true,
            fieldOrder: startingFieldOrder + 6,
            fieldAdditionalNote: 'preset.transport.mobilityDetails',
            isIdentity: true,
            isFromBackend: false,
            parentFieldId: mobilityRequiredId,
            parentFieldLabel: t('events.transportPreset.mobilityRequired'),
            parentOptionId: null,
            parentOptionLabel: t('events.toggleOptions.yes'),
            parentOptionValue: 1
        }
    ];
};
